import { useState } from 'react';
import type { WatchEvent } from '../../types';
import PageChanges from './PageChanges';
import { eventDetails } from './watchEventLines';

/** Alerts about pages, and which side of the check's page list they are. */
const PAGE_SIDE: Record<string, 'added' | 'removed'> = {
  new_section_launched: 'added',
  new_pages_published: 'added',
  pages_removed: 'removed',
};

/** How many pages of a list show before "and N more". */
const LIST_SHOWN = 10;

/** What an alert found, opened from its row (see `watchEventLines`). */
export default function EventDetails({ event }: { event: WatchEvent }) {
  const lines = eventDetails(event);
  // EVERY PAGE, not the ten the alert kept: the check's own page list.
  const side = PAGE_SIDE[event.kind];
  const [all, setAll] = useState(false);
  return (
    <>
      <dl
        className="mt-2 grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 border-t border-gray-100 pt-2 text-xs dark:border-gray-700"
        data-testid={`watch-event-details-${event.id}`}
      >
        {lines.map(line => (
          <div key={line.label} className="contents">
            <dt className="text-gray-500 dark:text-gray-400">{line.label}</dt>
            <dd className="min-w-0 text-gray-800 dark:text-gray-100">
              {line.value}
              {line.links && (
                <ul className="flex flex-col gap-0.5">
                  {line.links.slice(0, LIST_SHOWN).map(l => (
                    <li key={l.text} className="truncate">
                      {l.href ? (
                        <a
                          href={l.href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {l.text}
                        </a>
                      ) : (
                        l.text
                      )}
                    </li>
                  ))}
                  {line.links.length > LIST_SHOWN && (
                    <li className="text-gray-400">and {line.links.length - LIST_SHOWN} more</li>
                  )}
                </ul>
              )}
            </dd>
          </div>
        ))}
        <div className="contents">
          <dt className="text-gray-500 dark:text-gray-400">Found</dt>
          <dd className="text-gray-800 dark:text-gray-100">
            {event.detected_at ? new Date(event.detected_at).toLocaleString() : '-'}
          </dd>
        </div>
      </dl>
      {side && event.run_id ? (
        all ? (
          <PageChanges runId={event.run_id} only={side} />
        ) : (
          <button
            type="button"
            onClick={() => setAll(true)}
            className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
            data-testid={`watch-event-all-pages-${event.id}`}
          >
            Show every page {side === 'added' ? 'added' : 'removed'} in this check
          </button>
        )
      ) : null}
    </>
  );
}
